import { ChangeEvent, FormEvent, useRef, useState } from 'react';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  InputLabel,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import './MealForm.css';
import Meal from '../../models/meal';
import { Editor as TinyMCEEditor } from 'tinymce';
import { showNotification } from '../UI/General/showNotification';
import StarRating from './StarRating';
import HealthRating from './HealthRating';
import { RecipeFromImageResult, callSuggestHealthinessApi, callSuggestNutritionApi } from '../../api/assistant-api';
import IngredientInput, { createIngredientInputItem, IngredientInputItem } from './Ingredient/IngredientInput';
import TagSelector from './Tag/TagSelector';

interface MealFormProps {
  onMealSave: (meal: Meal) => void;
  submitMeal: (
    name: string,
    image: string,
    description: string,
    tastiness: number | null,
    healthiness: number | null,
    ingredients: IngredientInputItem[],
    tags: string[],
    calories: number | null,
    protein: number | null,
    carbs: number | null,
    fat: number | null,
    preparationTime: number | null
  ) => Promise<Meal>;
  getRecipeFromImage: (image: string) => Promise<RecipeFromImageResult>;
  initialName?: string;
  initialDescription?: string;
  initialImage?: string;
  initialTastiness?: number | null;
  initialHealthiness?: number | null;
  initialIngredients?: IngredientInputItem[];
  initialTags?: string[];
  initialCalories?: number | null;
  initialProtein?: number | null;
  initialCarbs?: number | null;
  initialFat?: number | null;
  initialPreparationTime?: number | null;
}

const Input = styled('input')({
  display: 'none',
});

const MealForm = (props: MealFormProps) => {
  const refName = useRef<HTMLInputElement>();
  const refDescription = useRef<TinyMCEEditor>();
  const [image, setImage] = useState(props.initialImage || '');
  const [tastiness, setTastiness] = useState<number | null>(props.initialTastiness ?? null);
  const [healthiness, setHealthiness] = useState<number | null>(props.initialHealthiness ?? null);
  const [ingredients, setIngredients] = useState<IngredientInputItem[]>(props.initialIngredients ?? []);
  const [description, setDescription] = useState(props.initialDescription || '');
  const [editingDescription, setEditingDescription] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingRecipe, setGeneratingRecipe] = useState(false);
  const [suggestingHealthiness, setSuggestingHealthiness] = useState(false);
  const [suggestedHealthiness, setSuggestedHealthiness] = useState<number | null>(null);
  const [tags, setTags] = useState<string[]>(props.initialTags ?? []);
  const [calories, setCalories] = useState<number | null>(props.initialCalories ?? null);
  const [protein, setProtein] = useState<number | null>(props.initialProtein ?? null);
  const [carbs, setCarbs] = useState<number | null>(props.initialCarbs ?? null);
  const [fat, setFat] = useState<number | null>(props.initialFat ?? null);
  const [preparationTime, setPreparationTime] = useState<number | null>(props.initialPreparationTime ?? null);
  const [suggestingNutrition, setSuggestingNutrition] = useState(false);
  const [suggestedNutrition, setSuggestedNutrition] = useState<{
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  } | null>(null);

  const submitHandler = async (event: FormEvent) => {
    event.preventDefault();
    if (refName === undefined) {
      return;
    }

    setSaving(true);
    try {
      const name = refName.current!.value;
      const meal = await props.submitMeal(
        name,
        image,
        description,
        tastiness,
        healthiness,
        ingredients,
        tags,
        calories,
        protein,
        carbs,
        fat,
        preparationTime
      );

      props.onMealSave(meal);
      showNotification({ type: 'success', message: 'Recept byl uložen' });
    } finally {
      setSaving(false);
    }
  };

  const saveFileAsBase64 = (file: File, callback: (result: string | ArrayBuffer | null) => void) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      // TODO: use reducer to
      callback(reader.result);
    };
    reader.onerror = function (error) {
      console.log('Error: ', error);
    };
  };

  const recipeImageChanged = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files === null) {
      return;
    }

    saveFileAsBase64(event.target.files[0], (result) => setImage(result!.toString()));
  };

  const recipeDescriptionImageChanged = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files === null) {
      return;
    }

    setGeneratingRecipe(true);
    saveFileAsBase64(event.target.files[0], async (result) => {
      try {
        const {
          name,
          recipe,
          ingredients: aiIngredients,
          preparationTime: aiPreparationTime,
        } = await props.getRecipeFromImage(result!.toString());
        if (refName.current) {
          refName.current.value = name;
        }
        setDescription(recipe);
        if (refDescription.current) {
          refDescription.current.setContent(recipe);
        }

        if (aiIngredients && aiIngredients.length > 0) {
          setIngredients(
            aiIngredients.map((ai) =>
              createIngredientInputItem({
                name: ai.name,
                quantity: ai.quantity,
              })
            )
          );
        }

        setPreparationTime(aiPreparationTime ?? null);
      } catch (e) {
        showNotification({
          type: 'error',
          message: 'Nepodařilo se získat popis receptu z obrázku',
        });
      } finally {
        setGeneratingRecipe(false);
      }
    });
  };

  return (
    <Box className="meal-form" component="form" onSubmit={submitHandler}>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {generatingRecipe ? (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              disabled
              sx={{ border: '2px solid', borderColor: 'secondary.dark' }}
            >
              <CircularProgress size={16} sx={{ mr: 0.5, color: 'inherit' }} />
              Generuji recept...
            </Button>
          ) : (
            <>
              <label htmlFor="recipeFromImage">
                <Input accept="image/*" id="recipeFromImage" type="file" onChange={recipeDescriptionImageChanged} />
                <Button
                  variant="contained"
                  color="secondary"
                  component="span"
                  size="small"
                  disabled={generatingRecipe}
                  sx={{ border: '2px solid', borderColor: 'secondary.dark' }}
                >
                  Vygenerovat z fotky receptu
                </Button>
              </label>
              <Tooltip title="Vyfotit recept">
                <label htmlFor="recipeFromCamera">
                  <Input
                    accept="image/*"
                    capture="environment"
                    id="recipeFromCamera"
                    type="file"
                    onChange={recipeDescriptionImageChanged}
                  />
                  <IconButton color="secondary" component="span" size="small">
                    <CameraAltIcon />
                  </IconButton>
                </label>
              </Tooltip>
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            id="name"
            label="Název"
            className="meal-form__name"
            defaultValue={props.initialName || ''}
            inputRef={refName}
          />
        </Grid>

        <Grid item xs={6}>
          <InputLabel>Chuťovost</InputLabel>
          <StarRating value={tastiness} readOnly={false} onChange={setTastiness} />
        </Grid>

        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <InputLabel>Zdravost</InputLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Navrhnout zdravost">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={suggestingHealthiness || !description}
                  sx={{ minWidth: 0, p: 0.5 }}
                  onClick={async () => {
                    setSuggestingHealthiness(true);
                    try {
                      const name = refName.current?.value ?? '';
                      const ingredientNames = ingredients.filter((i) => i.name !== '').map((i) => i.name);
                      const result = await callSuggestHealthinessApi(name, description, ingredientNames);
                      setSuggestedHealthiness(result.healthiness);
                    } catch (e: any) {
                      if (e?.message?.includes('403') || e?.message?.includes('Insufficient permissions')) {
                        showNotification({
                          type: 'error',
                          message: 'Funkce dostupná pouze pro prémiové uživatele',
                        });
                      } else {
                        showNotification({
                          type: 'error',
                          message: 'Nepodařilo se navrhnout zdravost',
                        });
                      }
                    } finally {
                      setSuggestingHealthiness(false);
                    }
                  }}
                >
                  {suggestingHealthiness ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
                </Button>
              </span>
            </Tooltip>
            <HealthRating value={healthiness} readOnly={false} onChange={setHealthiness} />
          </Box>
          <Dialog open={suggestedHealthiness !== null} onClose={() => setSuggestedHealthiness(null)}>
            <DialogTitle>Navrhovaná zdravost</DialogTitle>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <HealthRating value={suggestedHealthiness} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSuggestedHealthiness(null)}>Zrušit</Button>
              <Button
                variant="contained"
                onClick={() => {
                  setHealthiness(suggestedHealthiness);
                  setSuggestedHealthiness(null);
                }}
              >
                Použít
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            type="number"
            label="Doba přípravy (min)"
            value={preparationTime ?? ''}
            onChange={(e) =>
              setPreparationTime(
                e.target.value === '' ? null : Math.max(1, Math.min(600, parseInt(e.target.value, 10) || 1))
              )
            }
            fullWidth
            size="small"
            inputProps={{ min: 1, max: 600 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InputLabel>Nutriční hodnoty (na porci)</InputLabel>
            <Tooltip title="Odhadnout výživové hodnoty">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={suggestingNutrition || !description}
                  sx={{ minWidth: 0, p: 0.5 }}
                  onClick={async () => {
                    setSuggestingNutrition(true);
                    try {
                      const name = refName.current?.value ?? '';
                      const ingredientNames = ingredients.filter((i) => i.name !== '').map((i) => i.name);
                      const result = await callSuggestNutritionApi(name, description, ingredientNames, null);
                      setSuggestedNutrition(result);
                    } catch (e: any) {
                      if (e?.message?.includes('403') || e?.message?.includes('Insufficient permissions')) {
                        showNotification({
                          type: 'error',
                          message: 'Funkce dostupná pouze pro prémiové uživatele',
                        });
                      } else {
                        showNotification({
                          type: 'error',
                          message: 'Nepodařilo se odhadnout nutriční hodnoty',
                        });
                      }
                    } finally {
                      setSuggestingNutrition(false);
                    }
                  }}
                >
                  {suggestingNutrition ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <TextField
                type="number"
                label="Kalorie (kcal)"
                value={calories ?? ''}
                onChange={(e) =>
                  setCalories(
                    e.target.value === '' ? null : Math.max(0, Math.min(10000, parseInt(e.target.value, 10) || 0))
                  )
                }
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 10000 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                type="number"
                label="Bílkoviny (g)"
                value={protein ?? ''}
                onChange={(e) =>
                  setProtein(
                    e.target.value === '' ? null : Math.max(0, Math.min(2000, parseInt(e.target.value, 10) || 0))
                  )
                }
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 2000 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                type="number"
                label="Sacharidy (g)"
                value={carbs ?? ''}
                onChange={(e) =>
                  setCarbs(
                    e.target.value === '' ? null : Math.max(0, Math.min(2000, parseInt(e.target.value, 10) || 0))
                  )
                }
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 2000 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                type="number"
                label="Tuky (g)"
                value={fat ?? ''}
                onChange={(e) =>
                  setFat(e.target.value === '' ? null : Math.max(0, Math.min(2000, parseInt(e.target.value, 10) || 0)))
                }
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 2000 }}
              />
            </Grid>
          </Grid>
          <Dialog open={suggestedNutrition !== null} onClose={() => setSuggestedNutrition(null)}>
            <DialogTitle>Odhadnuté nutriční hodnoty</DialogTitle>
            <DialogContent>
              {suggestedNutrition && (
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <Box>
                    Kalorie: <strong>{suggestedNutrition.calories} kcal</strong>
                  </Box>
                  <Box>
                    Bílkoviny: <strong>{suggestedNutrition.protein} g</strong>
                  </Box>
                  <Box>
                    Sacharidy: <strong>{suggestedNutrition.carbs} g</strong>
                  </Box>
                  <Box>
                    Tuky: <strong>{suggestedNutrition.fat} g</strong>
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSuggestedNutrition(null)}>Zrušit</Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (suggestedNutrition) {
                    setCalories(suggestedNutrition.calories);
                    setProtein(suggestedNutrition.protein);
                    setCarbs(suggestedNutrition.carbs);
                    setFat(suggestedNutrition.fat);
                  }
                  setSuggestedNutrition(null);
                }}
              >
                Použít
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>

        <Grid item xs={12}>
          <IngredientInput value={ingredients} onChange={setIngredients} />
        </Grid>

        <Grid item xs={12}>
          <TagSelector selectedTagIds={tags} onChange={setTags} />
        </Grid>

        <Grid item xs={12}>
          <label htmlFor="image">
            <Stack direction="row" spacing={0}>
              <Input accept="image/*" id="image" type="file" onChange={recipeImageChanged} />
              <Button variant="contained" component="span">
                Nahrát obrázek
              </Button>
              {image !== '' && <img className="meal-form__image" src={image} alt="Obrázek receptu" />}
            </Stack>
          </label>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InputLabel>Popis (recept)</InputLabel>
            {!editingDescription && (
              <Tooltip title="Upravit popis">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Upravit popis"
                  onClick={() => setEditingDescription(true)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {editingDescription ? (
            <>
              {/* @ts-ignore*/}
              <Editor
                apiKey="go9oqy69ulqf0t28vzu22mrk4gtvtocxlis4tw5d7nu11jdx"
                onInit={(evt, editor) => (refDescription.current = editor)}
                initialValue={description}
                onEditorChange={(content) => setDescription(content)}
                init={{
                  height: 500,
                  menubar: false,
                  plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount',
                  ],
                  toolbar: 'undo redo | bold italic | bullist numlist outdent indent | removeformat',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }',
                }}
              />
              <Button size="small" sx={{ mt: 1 }} onClick={() => setEditingDescription(false)}>
                Hotovo
              </Button>
            </>
          ) : (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                minHeight: 100,
                fontSize: 14,
                fontFamily: 'Helvetica,Arial,sans-serif',
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" onClick={submitHandler} disabled={saving}>
            {saving ? 'Ukládám...' : 'Uložit recept'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MealForm;
